<template>
  <div class="shifts-setting">
    <PageTitleExtra
      :title="$t('shiftClassSetting.title')"
      :tooltip="$t('shiftClassSetting.tooltip')"
      cyBtn1="new-shift-btn"
      :btn="$t('common.button.create.text')"
      @btnClick="openDialog('create')"
    />
    <FiltersContainer>
      <BaseElInput
        v-model="nameSearch"
        testName="search_name"
        clearable
        :placeholder="$t('shiftClassSetting.search.name.placeholder')"
        @keypress.enter.native="refresh(true)"
        @clear="refresh(true)"
      >
        <i
          slot="suffix"
          class="el-input__icon el-icon-search"
          @click="refresh(true)"
        />
      </BaseElInput>
    </FiltersContainer>

    <section>
      <BaseTable
        v-loading="loading.table"
        testName="預約排班班別"
        :data="tableData"
        :empty-text="$t('common.table.empty.text')"
      >
        <EmptyBlock slot="empty" />
        <BaseElTableColumn prop="name" :label="$t('shiftClassSetting.table.name.title')" align="center" />
        <BaseElTableColumn prop="code" :label="$t('shiftClassSetting.table.code.title')" align="center" />
        <BaseElTableColumn prop="order" :label="$t('shiftClassSetting.table.order.title')" align="center" />
        <BaseElTableColumn prop="color" :label="$t('shiftClassSetting.table.color.title')" align="center">
          <template slot-scope="scope">
            <div
              :style="
                `
            background: ${scope.row.color};
            width: 50%;
            height: 20px;
            margin: auto;
            `
              "
            />
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn
          prop="name"
          :label="$t('common.table.action.text')"
          fixed="right"
          width="110"
          align="center"
        >
          <template slot-scope="scope">
            <TableEditBtnGroup
              :testName="scope.row?.name"
              @edit="openDialog('update'),
                     syncFormData(scope.row),
                     (selectRow = scope.row),
                     findShiftsClass()"
              @delete=";(modal.delete = true), (selectRow = scope.row)"
            />
          </template>
        </BaseElTableColumn>
      </BaseTable>

      <Pagination
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="tableDataCount"
        @pageChange="refresh(false)"
      />
    </section>

    <!-- Dialog -->
    <el-dialog
      :title="dialogTitle"
      data-testid="edit-dialog_shiftClassSetting"
      :visible.sync="showDialog"
      :close-on-click-modal="false"
      @close="resetForm()"
    >
      <AiHintBlock
        v-if="dialogType === 'create'"
        :btnText="$t('shiftClassSetting.aiGenerateBtn')"
        :hintLoading="hintLoading"
        @confirm="handleConfirm"
      />
      <BaseElForm
        ref="form"
        v-loading="hintLoading"
        :model="formData"
        label-position="top"
        :rules="formRules"
      >
        <BaseElFormItem :label="$t('shiftClassSetting.table.name.title')" prop="name">
          <BaseElInput v-model="formData.name" testName="formData_name" :placeholder="$t('shiftClassSetting.dialog.name.placeholder')" />
        </BaseElFormItem>
        <BaseElFormItem :label="$t('shiftClassSetting.dialog.color.title')" prop="color">
          <template slot="label">
            <FormItemTooltipLabel
              :label="$t('shiftClassSetting.dialog.color.title')"
              class="title"
            >
              {{ $t('shiftClassSetting.dialog.color.tooltip') }}
            </FormItemTooltipLabel>
          </template>
          <BaseElSelect
            v-model="formData.color"
            testName="formData_color"
            :placeholder="$t('shiftClassSetting.dialog.color.placeholder')"
            :no-data-text="$t('common.select.empty.text')"
            class="color"
          >
            <BaseElSelectOption
              v-for="item in shiftColors"
              :key="item.name"
              :label="item.name"
              :value="item.name"
            >
              <div
                :style="
                  `margin:5px 0; background: ${item.code}; color: white; width:100%; height: 28px; border-radius:4px;`
                "
              />
            </BaseElSelectOption>
          </BaseElSelect>
        </BaseElFormItem>
        <BaseElFormItem :label="$t('shiftClassSetting.dialog.code.title')" prop="code">
          <BaseElInput v-model="formData.code" testName="formData_code" :placeholder="$t('shiftClassSetting.dialog.code.placeholder')" />
        </BaseElFormItem>
        <BaseElFormItem :label="$t('shiftClassSetting.dialog.order.title')" prop="order">
          <BaseElInput v-model="formData.order" type="number" testName="formData_order" :placeholder="$t('shiftClassSetting.dialog.order.placeholder')" />
        </BaseElFormItem>
        <div class="mb-2">
          <BaseElButton plain @click="addTimes">{{ $t('shiftClassSetting.button.addPeriod.text') }}</BaseElButton>
        </div>
        <span class="flex text-sm leading-[17.38px] my-[8px]">{{ $t('shiftClassSetting.dialog.addPeriod.warning.text') }}</span>
        <BaseElFormItem prop="times" label-width="0">
          <div
            v-for="(item, index) in formData.times"
            :key="index + item.start"
            class="flex align-items-stretch mb-2"
          >
            <BaseElSelect
              v-model="item.start"
              filterable
              class="w-full"
              clearable
              :placeholder="$t('shiftClassSetting.dialog.start.placeholder')"
              @change="item.end = ''"
            >
              <BaseElSelectOption
                v-for="time in times"
                :key="time"
                :label="time"
                :value="time"
              />
            </BaseElSelect>
            <BaseElSelect
              v-model="item.end"
              filterable
              class="w-full ml-2"
              clearable
              :placeholder="$t('shiftClassSetting.dialog.end.placeholder')"
            >
              <BaseElSelectOption
                v-for="time in endTimes(item.start)"
                :key="time"
                :label="time"
                :value="time"
              />
            </BaseElSelect>
            <BaseElButton
              icon="el-icon-close"
              class="p-2 ml-2"
              @click="formData.times.splice(index, 1)"
            />
          </div>
        </BaseElFormItem>
      </BaseElForm>

      <div slot="footer" class="dialog-footer">
        <BaseElButton
          testName="footer-cancel-btn"
          plain
          @click=";(showDialog = false), resetForm()"
        >
          {{ $t('common.button.cancel.text') }}
        </BaseElButton>
        <BaseElButton testName="footer-confirm-btn" type="primary" @click="dialogConfirm">
          {{
            dialogType === 'create' ? $t('common.button.create.text') : $t('common.button.save.text')
          }}
        </BaseElButton>
      </div>
    </el-dialog>

    <!-- <DeleteDialog
      v-if="deleteDialog"
      :title="$t('common.dialog.delete.title')"
      :content="$t('common.dialog.delete.content')"
      width="40%"
      @close="deleteDialog = false"
      @delete=";(deleteDialog = false), deleteShiftsClass()"
    /> -->

    <DeleteShiftClassModal
      v-if="modal.delete"
      :selectRow="selectRow"
      @close="modal.delete =false"
      @refresh="refresh"
    />
  </div>
</template>

<script>
// import DeleteDialog from '@/components/Dialog/DeleteDialog'
import EmptyBlock from '@/components/EmptyBlock.vue'
import FormItemTooltipLabel from '@/components/Form/FormItemTooltipLabel.vue'
import {
  CreateShiftsClass,
  GetShiftsClass,
  GetShiftsClassCount,
  DeleteShiftsClass,
  UpdateShiftsClass,
  FindShiftsClass,
} from '@/api/shiftsClass'
import { isDigitRules, noEmptyRules, noEmptyTimeInterval } from '@/validation'
// Utils
// import { dialogTitle } from '@/utils/dialog'
// import { pageStartIndex } from '@/utils/table'
import formUtils from '@/utils/form'
import { timesConvert, plusZero } from '@/utils/helper'
// import { mapGetters } from 'vuex'
import { shiftColors } from '@/config/reservation'
import { find, map, get, set } from 'lodash'
import { usePermissions } from '@/use/permissions'
import { computed, ref, reactive, onMounted } from 'vue'
import { useTable } from '@/use/table'
import AiHintBlock from '@/components/AiHintBlock.vue'
import { useAi } from '@/use/useAi'
import { hint } from '@/config/hint'
import { i18n } from '@/plugins/i18n/i18n'
import DeleteShiftClassModal from './components/DeleteShiftClassModal.vue'

export default {
  name: 'ShiftClassSetting',
  components: {
    DeleteShiftClassModal,
    EmptyBlock,
    AiHintBlock,
    FormItemTooltipLabel,
  },
  setup () {
    const { checkAction } = usePermissions()
    const formData = ref({
      name: '',
      code: '',
      color: '',
      times: [],
      order: 100,
    })
    const formRules = reactive({
      name: noEmptyRules(),
      color: noEmptyRules(),
      code: noEmptyRules(),
      times: noEmptyTimeInterval(),
      order: [noEmptyRules(), isDigitRules()],
    })
    const {
      tableData,
      tableOptions,
      tableDataCount,
      loading,
      pageStartIndex,
      shopId,
    } = useTable()
    const form = ref(null)
    const { configurationHint, hintLoading, setData } = useAi()
    const dialogType = ref('create')
    const showDialog = ref(false)
    const nameSearch = ref('')
    const selectRow = ref(null)
    const selectShiftClass = ref(null)
    const deleteDialog = ref(false)
    const modal = reactive({
      delete: false,
    })
    const dialogTitle = computed(() => {
      if (dialogType.value === 'create') return i18n.t('shiftClassSetting.dialog.create.title')
      if (dialogType.value === 'update') return i18n.t('shiftClassSetting.dialog.edit.title')
      return ''
    })

    const useCrossDayShift = computed(() => checkAction('admin.appointmentScheduleDay.allowTimeCrossDay'))
    const times = computed(() => {
      const timesArray = []
      const limit = useCrossDayShift.value ? 48 * 6 + 1 : 24 * 6 + 1

      for (let i = 0; i < limit; i++) {
        const hour = plusZero(Math.floor(i / 6))
        const minute = (i % 6) * 10
        let label = `${hour}:${plusZero(minute)}`

        if (useCrossDayShift.value && hour >= 24) {
          label = `${plusZero(hour - 24)}:${plusZero(minute)}${i18n.t('shiftClassSetting.timeArray.tomorrow')}`
        }

        timesArray.push(label)
      }

      return timesArray
    })
    const endTimes = (start) => {
      if (!start) {
        return times.value
      }
      const startIndex = times.value.indexOf(start)
      return times.value.slice(startIndex + 1)
    }
    const handleConfirm = async (payload) => {
      const res = await configurationHint(get(hint, 'appointmentScheduleDay.key'), payload)
      if (!res) {
        hintLoading.value = false
        return
      }
      setData(formData.value, res)
    }
    const checkForm = async () => {
      return await formUtils.checkForm(form.value)
    }
    const resetForm = () => {
      formUtils.resetForm(form.value)
      formData.value = {
        name: '',
        code: '',
        color: '',
        times: [],
        order: 100,
      }
    }
    const dialogConfirm = async () => {
      if (!(await checkForm())) return
      const type = dialogType.value
      const payload = {
        shopId: shopId.value,
        id: get(selectRow.value, 'id'),
        name: get(formData.value, 'name'),
        code: get(formData.value, 'code'),
        color: find(shiftColors, { name: get(formData.value, 'color') }) ? find(shiftColors, { name: get(formData.value, 'color') }).code : get(formData.value, 'color'),
        order: formData.value.order,
        times: map(get(formData.value, 'times'), (item) => ({
          start: transformTime(item.start),
          end: transformTime(item.end),
        })),
      }
      if (type === 'create') createShiftsClass(payload)
      if (type === 'update') updateShiftsClass(payload)
      resetForm()
      showDialog.value = false
    }

    const createShiftsClass = async (payload) => {
      try {
        await CreateShiftsClass(payload)
        await refresh()
      } catch (error) {
        window.$message.error(i18n.t('common.message.createFail', { message: error || i18n.t('common.message.unknownError') }))
        console.log('錯誤', error)
      }
    }
    const updateShiftsClass = async (payload) => {
      try {
        await UpdateShiftsClass(payload)
        await refresh()
        window.$message.success(i18n.t('common.message.updateSuccess'))
      } catch (error) {
        window.$message.error(i18n.t('common.message.updateFail', { message: error || i18n.t('common.message.unknownError') }))
      }
    }

    const refresh = async (search = false) => {
      if (search) tableOptions.page = 1
      loading.table = true
      await getTableData()
      loading.table = false
    }
    const getTableData = async () => {
      const payload = {
        shopId: shopId.value,
        start: pageStartIndex.value,
        limit: tableOptions.pageLimit,
        name: nameSearch.value === '' ? undefined : nameSearch.value,
      }
      await Promise.allSettled([
        getShiftsClass(payload),
        getShiftsClassCount(payload),
      ])
    }
    const getShiftsClass = async (payload) => {
      try {
        const res = await GetShiftsClass(payload)
        tableData.value = res
      } catch (error) {
        console.log(error)
        window.$message.warning(error)
      }
    }
    const getShiftsClassCount = async (payload) => {
      try {
        const res = await GetShiftsClassCount(payload)
        tableDataCount.value = res.count
      } catch (error) {
        console.log(error)
        window.$message.warning(error)
      }
    }

    const transformTime = (time) => {
      if (time.includes(i18n.t('shiftClassSetting.timeArray.tomorrow'))) {
        const [hour, minute] = time.replace(i18n.t('shiftClassSetting.timeArray.tomorrow'), '').split(':')
        const newHour = parseInt(hour, 10) + 24
        return `${newHour}:${minute}`
      }
      return time
    }
    const syncFormData = (row) => {
      set(formData.value, 'name', row.name)
      set(formData.value, 'code', row.code)
      const color = find(shiftColors, { code: row.color })
      set(formData.value, 'color', color ? color.name : '')
      set(formData.value, 'order', row.order || '-')
    }
    const findShiftsClass = async () => {
      try {
        const res = await FindShiftsClass({
          shopId: shopId.value,
          id: get(selectRow.value, 'id'),
        })

        selectShiftClass.value = res
        set(formData.value, 'times', map(res.AppointmentScheduleDayTimes, (item) => {
          return {
            start: transformBackTime(timesConvert(item.start)),
            end: transformBackTime(timesConvert(item.end)),
          }
        }))
      } catch (error) {
        console.log(error)
        window.$message.warning(error)
      }
    }
    const deleteShiftsClass = async () => {
      try {
        await DeleteShiftsClass({
          shopId: get(selectRow.value, 'ShopId'),
          id: get(selectRow.value, 'id'),
        })

        await refresh()
        window.$message.success(i18n.t('common.message.deleteSuccess'))
      } catch (error) {
        window.$message.error(i18n.t('common.message.createFail2', { message: error || i18n.t('common.message.unknownError') }))
        console.log('錯誤', error)
      }
    }
    const transformBackTime = (time) => {
      const [hour, minute] = time.split(':')
      let newHour = parseInt(hour, 10)
      let nextDay = ''
      if (newHour >= 24) {
        newHour -= 24
        nextDay = i18n.t('shiftClassSetting.timeArray.tomorrow')
      }
      return `${newHour.toString().padStart(2, '0')}:${minute}${nextDay}`
    }
    const openDialog = (type) => {
      dialogType.value = type
      showDialog.value = true
    }
    const addTimes = () => {
      const times = get(formData.value, 'times')
      let start
      if (times.length === 0) {
        start = ''
      } else {
        start = times[times.length - 1].end || ''
      }
      times.push({ start, end: '' })
    }
    onMounted(async () => {
      await refresh()
    })
    return {
      useCrossDayShift,
      times,
      endTimes,
      configurationHint,
      formData,
      hintLoading,
      dialogConfirm,
      form,
      showDialog,
      nameSearch,
      loading,
      tableData,
      tableDataCount,
      shiftColors,
      refresh,
      tableOptions,
      pageStartIndex,
      checkForm,
      resetForm,
      dialogType,
      createShiftsClass,
      updateShiftsClass,
      transformTime,
      syncFormData,
      findShiftsClass,
      selectShiftClass,
      selectRow,
      deleteShiftsClass,
      deleteDialog,
      openDialog,
      addTimes,
      formRules,
      dialogTitle,
      handleConfirm,
      modal,
    }
  },
}
</script>

<style scoped lang="postcss">
::v-deep .el-select .el-input {
  @apply w-auto;
}
</style>
